import { useState } from 'react'
import type { LigneDevis, LigneMetre } from '../db/modele'
import { LIGNE_METRE_VIDE, quantiteLigneMetre, totalMetre } from '../lib/metre'
import { versSaisie } from '../lib/money'
import { Bouton } from './composants/bouton'
import { Feuille } from './composants/feuille'
import { CHAMP } from './synchro'

/**
 * Métré d'une ligne de devis : des lignes « nombre × longueur × largeur × hauteur » (chaque valeur peut être une
 * expression comme 2,5+0,8) dont la somme devient la quantité. Les ouvertures se soustraient avec le signe « − ».
 */
export function FeuilleMetre(props: { ligne: LigneDevis; lectureSeule: boolean; appliquer: (quantite: number, metre: LigneMetre[]) => void; fermer: () => void }) {
  const [lignes, setLignes] = useState<LigneMetre[]>(props.ligne.metre?.length ? props.ligne.metre : [{ ...LIGNE_METRE_VIDE }])
  const total = totalMetre(lignes)
  const unite = props.ligne.unite ?? 'u'
  const maj = (i: number, patch: Partial<LigneMetre>) => setLignes((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)))

  return (
    <Feuille ouverte onChange={(o) => !o && props.fermer()} titre={`Métré : ${props.ligne.designation}`}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-slate-700">
          Nombre × longueur × largeur × hauteur : laissez vides les dimensions inutiles (surface : longueur et largeur ; linéaire : longueur seule). Vous pouvez écrire un calcul : 2,5+0,8.
        </p>
        <ul className="flex flex-col gap-3">
          {lignes.map((l, i) => {
            const r = quantiteLigneMetre(l)
            return (
              <li key={i} className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <input className={CHAMP} aria-label={`Libellé de la ligne ${i + 1}`} placeholder="Pièce ou zone (facultatif)" value={l.libelle} maxLength={100} disabled={props.lectureSeule} onChange={(e) => maj(i, { libelle: e.target.value })} />
                  {!props.lectureSeule && lignes.length > 1 && (
                    <button className="grid size-12 shrink-0 place-items-center rounded text-xl text-slate-700" aria-label={`Retirer la ligne ${i + 1}`} onClick={() => setLignes((x) => x.filter((_, j) => j !== i))}>
                      ×
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  {(
                    [
                      ['nombre', 'Nombre', l.nombre],
                      ['longueur', 'Long.', l.longueur ?? ''],
                      ['largeur', 'Larg.', l.largeur ?? ''],
                      ['hauteur', 'Haut.', l.hauteur ?? ''],
                    ] as const
                  ).map(([cle, nom, valeur]) => (
                    <label key={cle} className="flex flex-col gap-1">
                      <span className="text-slate-700">{nom}</span>
                      <input className={`${CHAMP} px-2`} aria-label={`${nom} de la ligne ${i + 1}`} inputMode="decimal" value={valeur} disabled={props.lectureSeule} onChange={(e) => maj(i, { [cle]: e.target.value })} />
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    className="min-h-12 rounded-xl border-2 border-slate-400 px-4 font-semibold disabled:opacity-60"
                    aria-label={`Signe de la ligne ${i + 1} : ${l.signe === 1 ? 'ajoute' : 'soustrait'}`}
                    disabled={props.lectureSeule}
                    onClick={() => maj(i, { signe: l.signe === 1 ? -1 : 1 })}
                  >
                    {l.signe === 1 ? '+ ajoute' : '− soustrait'}
                  </button>
                  <span className={r.ok ? 'font-semibold' : 'text-sm text-red-800'} role={r.ok ? undefined : 'alert'}>
                    {r.ok ? `${versSaisie(r.quantite)} ${unite}` : r.erreur}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
        {!props.lectureSeule && (
          <Bouton variante="secondaire" onClick={() => setLignes((l) => [...l, { ...LIGNE_METRE_VIDE }])}>
            Ajouter une ligne
          </Bouton>
        )}
        <p className="text-lg font-bold" data-testid="total-metre">
          {total.ok ? `Total : ${versSaisie(total.quantite)} ${unite}` : <span className="text-base font-normal text-red-800">{total.erreur}</span>}
        </p>
        {!props.lectureSeule && (
          <Bouton
            disabled={!total.ok}
            onClick={() => {
              if (!total.ok) return
              // Libellés vides et dimensions vides ne sont pas conservés : seul ce qui sert à rééditer l'est
              props.appliquer(
                total.quantite.toNumber(),
                lignes.map((l) => ({ libelle: l.libelle.trim(), nombre: l.nombre.trim(), signe: l.signe, ...(l.longueur?.trim() && { longueur: l.longueur.trim() }), ...(l.largeur?.trim() && { largeur: l.largeur.trim() }), ...(l.hauteur?.trim() && { hauteur: l.hauteur.trim() }) })),
              )
              props.fermer()
            }}
          >
            Utiliser {total.ok ? versSaisie(total.quantite) : ''} comme quantité
          </Bouton>
        )}
      </div>
    </Feuille>
  )
}
